import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  PlayCircle,
  Smartphone,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Play,
  Heart,
  ShieldCheck,
  Users,
  Dog,
} from "lucide-react";
import TopNavBar from "../../components/TopNavBar";
import appIcon from "../../assets/appIcon.svg";
import videoThumbnail from "../../assets/video_thumbnail.png";
import "./HelpPage.css";
import MobileTopBar from "../../components/MobileTopBar";

const faqData = [
  {
    id: "dog_sharing",
    title: "Dog Sharing",
    icon: Dog,
    questions: [
      {
        id: "q1",
        q: "What even is dog sharing?",
        a: "Dog sharing is a concept where dog owners share the joy and responsibility of their dog with trusted, vetted individuals in their community. Think of it like a playdate arrangement — your dog gets extra love, walks, and attention from someone who adores dogs but might not be able to own one themselves.",
      },
      {
        id: "q2",
        q: "Why dog-share?",
        a: `Why Pup Pals dog-share:

Our Pup Pals (dog lovers without a full-time dog of their own) join Social Paws for many reasons, including:

• Exercise: Having a companion for walks, runs, and outdoor adventures.
• Companionship: Enjoying the comfort of spending time with a furry friend while working from home or relaxing.
• Family dynamics: Giving dog-loving children or family members the opportunity to enjoy dogs without the commitment of ownership.
• Missing their dogs: Staying connected to dogs after the loss of a beloved pet, or while living away from family dogs.
• Life changes: Finding companionship during retirement, after a move, a divorce, or other major life transitions.

Why Pup Pawrents dog-share:

Our Pup Pawrents (dog owners) often join Social Paws to:

• Give their dog more affection, attention, and enrichment.
• Provide their dog with additional walks, playtime, and companionship.
• Reduce the amount of time their dog spends alone.
• Save money on dog care, such as walking, daycare, or boarding.
• Enjoy occasional flexibility or a helping hand with dog parenting.`,
      },
      {
        id: "q3",
        q: "Who pays for the dog's needs: ie: dog grooming, food, treats, etc?",
        a: `Pup Pawrents are always responsible for their dog's expenses and overall care, including food, grooming, veterinary care, medications, and other day-to-day needs.

Pup Pals contribute their time, companionship, and care through walks, playdates, adventures, or other agreed-upon activities.

As dog-sharing relationships grow, it's not uncommon for Pup Pals to occasionally spoil their shared dog with a toy or treats—much like an aunt or uncle might. But these gestures are entirely optional.

Ultimately, the Pup Pawrent remains responsible for their dog's expenses and sets the care guidelines that Pup Pals are expected to follow.`,
      },
      {
        id: "q4",
        q: "Do the dogs enjoy dog-sharing?",
        a: `Many dogs thrive with dog-sharing. They often benefit from additional attention, affection, enrichment, exercise, and meaningful social interaction.

That said, every dog is different. Some dogs are naturally more social and adaptable than others. We encourage introducing dog-sharing gradually, paying close attention to your dog's comfort, behavior, and enthusiasm throughout the process.

As your dog's Pup Pawrent, you know them best. If your dog appears stressed, uncomfortable, or simply doesn't enjoy dog-sharing, it's okay to slow down, adjust the relationship, or decide it's not the right fit. Their wellbeing always comes first.`,
      },
    ],
  },
  {
    id: "platform_faqs",
    title: "Platform FAQs",
    icon: Smartphone,
    questions: [
      {
        id: "q5",
        q: "How does Social Paws work?",
        a: `Social Paws helps dog lovers find one another, build trust, and create meaningful dog-sharing relationships.

Using the app, you can discover nearby Pup Pawrents and Pup Pals, explore profiles, exchange messages, schedule walks, and leave reviews after real-life interactions.

Our goal isn't simply to match people - it's to help neighbors build lasting relationships through dogs, whether for occasional walks, playdates, or long-term dog-sharing. Social Paws provides the tools to help you connect safely and intentionally.`,
      },
      {
        id: "q6",
        q: "What is the Pup-Sharing Journey?",
        a: `Social Paws is built around the philosophy that: relationships come before responsibility.

We don't expect you to hand your dog off to a complete stranger. Instead, we guide members through a gradual journey that builds trust over time.

Milestone 1: Toss a Treat — Show interest in a dog or fellow dog lover by tossing a Treat. It's a friendly way to say, "I'd like to connect!"

Milestone 2: Take a Group Walk — Meet in person with the dog's Pup Pawrent for a walk together. This is your opportunity to get to know one another, ask questions, and see how everyone connects.

Milestone 3: Pass the Leash — When everyone feels comfortable, the member takes the dog on their first solo walk.

Milestone 4: Become a Trusted Pup Pal — When a Pup Pawrent designates someone as a Trusted Pup Pal, they're saying, "I trust you with my dog." Trusted Pup Pals can view the dog's availability calendar and instantly book available walks.

Progressing through the journey is always mutual. Both parties must agree before moving to the next milestone, and either person can choose to remain at their current milestone or end the journey at any time.`,
      },
    ],
  },
  {
    id: "safety_faqs",
    title: "Safety FAQs",
    icon: ShieldCheck,
    questions: [
      {
        id: "q7",
        q: "Is dog-sharing safe?",
        a: `Safety is our top priority, but like any new relationship, dog-sharing requires good judgment and trust.

Social Paws is designed to help neighbors build trust gradually - not rush into sharing responsibility. Members are encouraged to get to know one another through conversations, group walks, and solo walks before becoming Trusted Pup Pals.

We support safer connections through complete member profiles, detailed Pup Playbooks, Reviews, Reporting tools, and secure sign-in with Google or Apple.

Every member also plays an important role in keeping our community safe. Take time to ask questions, communicate openly, and only move forward when everyone - including the dog - feels comfortable.

The strongest dog-sharing relationships are built on honesty, communication, and trust.`,
      },
      {
        id: "q8",
        q: "What is the Pup Playbook?",
        a: `The Pup Playbook is a dog's care guide. It gives anyone spending time with your pup the information they need to provide safe, confident, and consistent care.

Your Pup Playbook may include:

• Daily routines and care instructions
• Feeding schedule and dietary needs
• Medications and medical conditions
• Veterinary contact information
• Emergency contacts
• Commands your dog knows
• Triggers, fears, and behaviors to be aware of
• Favorite toys, treats, and activities
• Walking preferences and care guidelines

The more complete your Pup Playbook, the more prepared and confident your Trusted Pup Pals will be.`,
      },
      {
        id: "q9",
        q: "What if an incident or emergency occurs during a dog-share?",
        a: `If an incident or emergency occurs while you're caring for a shared dog:

• Stay calm and prioritize everyone's safety.
• Contact the dog's Pup Pawrent immediately and explain what happened.
• Decide on the next steps together whenever possible.
• If the Pup Pawrent cannot be reached and veterinary care is needed, use the emergency contact and veterinary information provided in the Pup Playbook.
• If necessary, follow the appropriate procedures for your personal health, property, or pet insurance.

Clear, timely communication is essential. We encourage both parties to keep one another informed throughout the situation and work together in the dog's best interest.`,
      },
    ],
  },
  {
    id: "team_paws_faqs",
    title: "Pup Pawrent FAQs",
    icon: Users,
    questions: [
      {
        id: "q10",
        q: "Do I have to meet someone before they care for my dog?",
        a: `Yes. In fact, it's required.

Social Paws is built on relationship-building, not instant dog care. Every dog-sharing relationship moves through the Pup-Sharing Journey, where you and your potential Pup Pal get to know one another through real-life interactions before sharing responsibility.

Take as many group walks as you need before progressing to solo walks. Move forward only when everyone—including your dog—feels comfortable and confident.`,
      },
      {
        id: "q11",
        q: "Can I leave my dog with someone the first time we meet?",
        a: `No.

Your first meeting should always be a group walk. Use this time to get to know one another, answer questions, observe your dog's comfort level, and watch the other person interact with your dog before considering a solo walk.`,
      },
      {
        id: "q12",
        q: "Where should we meet?",
        a: `Choose a familiar public place where your dog feels comfortable, such as your regular walking route, a neighborhood park, or another dog-friendly location.

During your group walk, we encourage you to:

• Talk about your goals for dog-sharing.
• Introduce your dog's routines and personality.
• Demonstrate any commands or handling techniques.
• Allow the other person to practice holding the leash while you observe.`,
      },
      {
        id: "q13",
        q: "What information should I share about my dog?",
        a: `Share as much as possible.

Your Pup Playbook should include your dog's routines, personality, commands, medical needs, walking preferences, triggers, fears, and anything else that helps someone safely care for your dog.

Honesty leads to better matches and safer dog-sharing.`,
      },
      {
        id: "q14",
        q: "Can someone else let my dog off leash?",
        a: `Only if you're comfortable with it.

Unless you've specifically agreed otherwise, we recommend that shared dogs remain on leash outside of securely enclosed areas.

Remember, someone caring for your dog doesn't know them the way you do. Set clear expectations for when, where, and if your dog may ever be off leash.`,
      },
      {
        id: "q15",
        q: "Should I tip my Pup Pal?",
        a: `That's entirely up to you.

Social Paws is built around relationships, not transactions. While many members enjoy showing appreciation with treats, small gifts, or kind gestures, there is never an expectation to tip.

The greatest reward is building a trusted relationship that benefits both humans and dogs.`,
      },
      {
        id: "q16",
        q: "What vaccinations are required?",
        a: `To participate in Social Paws, dogs should be current on all vaccinations required by local laws and our Community Guidelines.

We recommend maintaining vaccinations commonly required in social settings, including:

• Rabies
• DA2PP (Distemper/Parvo combination)
• Bordetella

Keeping vaccinations current helps create a healthier and safer community for everyone.`,
      },
      {
        id: "q17",
        q: "Do you have breed restrictions?",
        a: `No. We welcome dogs of every breed.

What matters most is honest communication. Every dog has unique needs, personality traits, and behaviors, and it's the Pup Pawrent's responsibility to communicate those openly through their profile, Pup Playbook, and conversations with potential dog-sharing partners.`,
      },
      {
        id: "q18",
        q: "What if I think my dog wasn't treated appropriately?",
        a: `Start by having an open conversation with the other member to better understand what happened.

If you believe someone acted dishonestly, violated our Community Guidelines, or placed your dog at risk, please leave an honest review and report the profile so our team can investigate.

The safety and wellbeing of dogs always comes first.`,
      },
    ],
  },
  {
    id: "team_social_faqs",
    title: "Pup Pal FAQs",
    icon: Heart,
    questions: [
      {
        id: "q19",
        q: "Do I have to meet the Pup Pawrent before caring for their dog?",
        a: `Yes. In fact, it's required.

Every dog-sharing relationship begins with the Pup-Sharing Journey. Meet as many times as you'd like through group walks before progressing to solo walks. Use these walks to get to know the Pup Pawrent, build trust, learn about the dog's personality, and gain confidence handling the dog in different situations.

Move forward only when everyone—including the dog—feels comfortable and excited to do so.`,
      },
      {
        id: "q20",
        q: "What should the Pup Pawrent provide?",
        a: `The Pup Pawrent is responsible for providing everything needed for their dog's care, including items like a leash, harness, food, medications, toys, treats, or anything specific to the activity you've planned.

When in doubt, simply ask. Good communication makes for great dog-sharing.`,
      },
      {
        id: "q21",
        q: "Are the dogs vaccinated?",
        a: `Yes. We require dogs on Social Paws to be current on the vaccinations required by local laws and our Community Guidelines.

If you have questions about a dog's vaccination history or health, we encourage you to discuss them with the Pup Pawrent before moving forward.`,
      },
      {
        id: "q22",
        q: "Can I let the dog off leash?",
        a: `Only if the Pup Pawrent has specifically given permission.

Unless instructed otherwise, shared dogs should remain on leash outside of securely enclosed areas.

Always follow the care guidelines provided in the dog's Pup Playbook.`,
      },
      {
        id: "q23",
        q: "Where can I take the dog?",
        a: `Always ask first.

Every Pup Pawrent has different comfort levels and care preferences. Before taking a dog on a hike, introducing them to other animals, visiting new places, offering new foods, or making changes to their routine, check with the Pup Pawrent.

When in doubt, ask. If you don't receive an answer, don't assume.`,
      },
      {
        id: "q24",
        q: "What if I think a dog is being neglected?",
        a: `If you're concerned about a dog's wellbeing, start by having a respectful conversation with the Pup Pawrent. There may be information you're unaware of.

If you believe a dog is being neglected, abused, or placed at risk, please report the profile so our team can review the situation.

Protecting the wellbeing of dogs is one of our highest priorities.`,
      },
    ],
  },
];

const HelpPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [playingVideo, setPlayingVideo] = useState(null);
  const [permissionNotice, setPermissionNotice] = useState("");
  const enableLocation = () => {
    if (!navigator.geolocation) {
      setPermissionNotice("Location is unavailable in this browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      localStorage.setItem("user_location_coords", JSON.stringify({ lat: coords.latitude, lng: coords.longitude }));
      window.dispatchEvent(new Event("locationGranted"));
      setPermissionNotice("Location is enabled. You can now find your neighborhood pack.");
    }, () => setPermissionNotice("Allow location for this website in your browser’s site settings, then try again."), { enableHighAccuracy: true, timeout: 10000 });
  };
  const enableNotifications = async () => {
    if (!("Notification" in window)) {
      setPermissionNotice("Notifications are unavailable in this browser. On iPhone, add Social Paws to your Home Screen and open it there first.");
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setPermissionNotice(permission === "granted"
        ? "Notification permission is enabled. Open the map to finish notification setup."
        : "Allow notifications for this website in your browser’s site settings, then try again.");
    } catch {
      setPermissionNotice("Unable to request notifications. Check your browser’s site settings.");
    }
  };

  // States for View Switching and Accordions
  const [activeView, setActiveView] = useState(
    location.state?.initialView || "main",
  ); // "main" | "faq" | "guidelines"
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeView]);

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else if (activeView !== "main") {
      setActiveView("main");
      setPlayingVideo(null);
      setExpandedCategory(null);
      setExpandedQuestion(null);
    } else {
      navigate("/dashboard-switcher");
    }
  };

  const toggleCategory = (id) => {
    setExpandedCategory((prev) => (prev === id ? null : id));
    setExpandedQuestion(null);
  };

  const toggleQuestion = (id) => {
    setExpandedQuestion((prev) => (prev === id ? null : id));
  };

  return (
    <div className="help-container">
      {/* DESKTOP Header */}
      <div
        className="list-desktop-only"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          boxSizing: "border-box",
          position: "relative",
          zIndex: 10,
          padding: "0 1.5rem",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "10px",
            marginTop: "16px",
            cursor: "pointer",
          }}
          onClick={handleBack}
        >
          {activeView !== "main" && (
            <ArrowLeft size={24} color="#fff" strokeWidth={3} />
          )}
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "28px", height: "28px", objectFit: "contain" }}
          />
          <span
            style={{
              color: "#fff",
              fontSize: "22px",
              fontWeight: "700",
            }}
          >
            Social Paws
          </span>
        </div>

        <TopNavBar theme="dark" />
      </div>

      {/* MOBILE Header */}
      <MobileTopBar onBack={handleBack} backColor="#fff" logoColor="#fff" />
      <div
        className="list-mobile-header list-mobile-only"
        style={{ padding: "0 1rem", marginTop: "-8px" }}
      >
        <TopNavBar theme="dark" />
      </div>

      {/* Content Area - Added ref here */}
      <div className="help-content-scroll" ref={scrollContainerRef}>
        {activeView === "main" ? (
          <>
            <div className="help-main-intro">
              <h1>How can we help?</h1>
              <p>Search our knowledge base or browse<br />categories below.</p>
            </div>
            <div
              className="help-menu-item"
              onClick={() =>
                navigate("/install-prompt-page", { state: { from: "/help" } })
              }
            >
              <div className="help-icon-wrapper">
                <Smartphone size={24} color="#009fd9" strokeWidth={2} />
              </div>
              <div className="help-item-text">
                <h3>Add to Home Screen</h3>
                <p>Keep Social Paws a tap away</p>
              </div>
              <ChevronRight size={20} color="#009fd9" />
            </div>

            <button type="button" className="help-menu-item help-action-card" onClick={enableLocation}>
              <div className="help-icon-wrapper"><Smartphone size={24} color="#009fd9" strokeWidth={2} /></div>
              <div className="help-item-text"><h3>Turn on Location</h3><p>Find your neighborhood pack</p></div>
              <ChevronRight size={20} color="#009fd9" />
            </button>
            <button type="button" className="help-menu-item help-action-card" onClick={enableNotifications}>
              <div className="help-icon-wrapper"><Smartphone size={24} color="#009fd9" strokeWidth={2} /></div>
              <div className="help-item-text"><h3>Turn on Notifications</h3><p>Know when someone barks at you</p></div>
              <ChevronRight size={20} color="#009fd9" />
            </button>
            {permissionNotice && <p className="help-permission-notice" role="status">{permissionNotice}</p>}
            {/* WATCH APP TOUR */}
            <div
              className="help-menu-item tour-item"
            >
              <div className="tour-header">
                <div className="help-icon-wrapper">
                  <PlayCircle size={24} color="#009fd9" strokeWidth={2} />
                </div>
                <div className="help-item-text">
                  <h3>Watch App Tour</h3>
                  <p>60-second walkthrough</p>
                </div>
                <ChevronRight size={20} color="#009fd9" />
              </div>

              <div className="tour-video-card">
                {playingVideo !== "tour" ? (
                  <>
                    <img
                      src={videoThumbnail}
                      alt="App Tour Video"
                      className="tour-video-thumb"
                      onClick={() => setPlayingVideo("tour")}
                      onError={(e) => {
                        e.target.src =
                          "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&q=80&w=800";
                      }}
                    />
                    <button
                      className="tour-play-btn"
                      aria-label="Play app tour"
                      onClick={() => setPlayingVideo("tour")}
                    >
                      <Play size={32} fill="#000" color="#000" />
                    </button>
                  </>
                ) : (
                  <iframe
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      zIndex: 10,
                    }}
                    src="https://www.youtube.com/embed/oCiNnGTkGFM?playsinline=1&controls=1&rel=0&autoplay=1"
                    title="App Tour Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                )}
              </div>
            </div>

            {/* WATCH APP TUTORIAL */}
            <div
              className="help-menu-item tour-item"
            >
              <div className="tour-header">
                <div className="help-icon-wrapper">
                  <PlayCircle size={24} color="#009fd9" strokeWidth={2} />
                </div>
                <div className="help-item-text">
                  <h3>Watch App Tutorial</h3>
                  <p>60-second tutorial</p>
                </div>
                <ChevronRight size={20} color="#009fd9" />
              </div>

              <div className="tour-video-card">
                {playingVideo !== "tutorial" ? (
                  <>
                    <img
                      src={videoThumbnail}
                      alt="App Tutorial Video"
                      className="tour-video-thumb"
                      onClick={() => setPlayingVideo("tutorial")}
                      onError={(e) => {
                        e.target.src =
                          "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&q=80&w=800";
                      }}
                    />
                    <button
                      className="tour-play-btn"
                      aria-label="Play app tutorial"
                      onClick={() => setPlayingVideo("tutorial")}
                    >
                      <Play size={32} fill="#000" color="#000" />
                    </button>
                  </>
                ) : (
                  <iframe
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      zIndex: 10,
                    }}
                    src="https://www.youtube.com/embed/1X2dFnRdEY0?playsinline=1&controls=1&rel=0&autoplay=1"
                    title="App Tutorial Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  ></iframe>
                )}
              </div>
            </div>

            <div
              className="help-menu-item"
              onClick={() => {
                setActiveView("guidelines");
                setPlayingVideo(null);
              }}
            >
              <div className="help-icon-wrapper">
                <Smartphone size={24} color="#009fd9" strokeWidth={2} />
              </div>
              <div className="help-item-text">
                <h3>Community Guidelines</h3>
                <p>Our rules and standards</p>
              </div>
              <ChevronRight size={20} color="#009fd9" />
            </div>

            <div
              className="help-menu-item"
              onClick={() => {
                setActiveView("faq");
                setPlayingVideo(null);
              }}
            >
              <div className="help-icon-wrapper">
                <PlayCircle size={24} color="#009fd9" strokeWidth={2} />
              </div>
              <div className="help-item-text">
                <h3>FAQ</h3>
                <p>Common questions answered</p>
              </div>
              <ChevronRight size={20} color="#009fd9" />
            </div>

          </>
        ) : activeView === "faq" ? (
          <>
            {/* FAQ VIEW */}
            <div className="faq-view-container">
              <h2 style={{ color: "#fff", textAlign: "center" }}>
                How can we help?
              </h2>
              <p style={{ color: "#fff", textAlign: "center" }}>
                Search our knowledge base or browse categories below.
              </p>
              {faqData.map((category) => (
                <div key={category.id} className="faq-category-card">
                  <div
                    className="faq-category-header"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="faq-header-left">
                      <div className="faq-icon-circle">
                        <category.icon
                          size={22}
                          color="#009fd9"
                          strokeWidth={2}
                        />
                      </div>
                      <span className="faq-category-title">
                        {category.title}
                      </span>
                    </div>
                    {expandedCategory === category.id ? (
                      <ChevronUp size={24} color="#009fd9" strokeWidth={2.5} />
                    ) : (
                      <ChevronDown
                        size={24}
                        color="#009fd9"
                        strokeWidth={2.5}
                      />
                    )}
                  </div>

                  {expandedCategory === category.id && (
                    <div className="faq-questions-container">
                      {category.questions.length > 0 ? (
                        category.questions.map((q) => (
                          <div key={q.id} className="faq-question-wrapper">
                            <div
                              className="faq-question-header"
                              onClick={() => toggleQuestion(q.id)}
                            >
                              <Heart
                                size={20}
                                color="#009fd9"
                                strokeWidth={2}
                                className="faq-question-icon"
                              />
                              <span className="faq-question-text">{q.q}</span>
                            </div>
                            {expandedQuestion === q.id && (
                              <div
                                className="faq-answer-box"
                                style={{ whiteSpace: "pre-line" }}
                              >
                                {q.a}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div
                          className="faq-answer-box"
                          style={{ paddingTop: "10px" }}
                        >
                          More info coming soon.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            {/* COMMUNITY GUIDELINES VIEW */}
            <div className="cg-view-container">
              <h1 className="cg-main-title">
                🐾 SOCIAL PAWS
                <br />
                COMMUNITY
                <br />
                GUIDELINES
              </h1>
              <h3 className="cg-subtitle">
                A community built on sharing the love of dogs
              </h3>
              <p className="cg-body-text">
                Social Paws exists to help Pup Pawrents get a helping paw from
                neighbors — saving money, easing pet-parent guilt, and creating a
                layer of support.
                <br /><br />
                At the same time, we want to help Pup Pals — dog lovers who don’t
                currently have a dog of their own — experience the benefits of
                spending time with one.
                <br /><br />
                That means our Pup Pawrents are open to sharing the love of their
                dogs with dog-loving neighbors, while our Pup Pals are open to
                sharing their time and care with neighborhood pups.
                <br /><br />
                These guidelines are here to help keep our community safe,
                respectful, and successful for everyone — humans and pups alike. 🐾
              </p>

              <div className="cg-section-divider">
                <h2>💛 GOOD COMMUNITY!</h2>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">1</div>
                  <h4>Non-Owners — We See You</h4>
                </div>
                <p className="cg-body-text">
                  Social Paws embraces non-owners - because many of our Pup Pals
                  are former dog owners who have lost a beloved pup or whose dogs
                  are temporarily away from them. We believe our Pup Pals are
                  responsible, compassionate dog lovers who can enrich our dogs’
                  lives when given the opportunity.
                </p>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">2</div>
                  <h4>Honest Profiles</h4>
                </div>
                <p className="cg-body-text">
                  Pup profiles should include the good, the quirky, and the challenging.
                </p>
                <p className="cg-body-text" style={{ marginTop: "1rem" }}>
                  If a dog:
                </p>
                <ul className="cg-bullet-list">
                  <li>isn’t friendly with other dogs</li>
                  <li>needs to stay on a close leash</li>
                  <li>has behavioral or medical considerations</li>
                </ul>
                <p className="cg-body-text" style={{ marginTop: "1rem" }}>
                  those details should be fully disclosed.
                  <br />
                  Transparency creates safety, builds trust, and helps foster
                  connections where everyone knows what to expect and is happy to
                  accommodate.
                </p>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">3</div>
                  <h4>Intentional Interactions</h4>
                </div>
                <p className="cg-body-text">
                  Social Paws is about creating real-life connections. Tossing
                  treats, liking profiles, and messaging are just the beginning —
                  leading to real-life dog walks where compatibility can become clear.
                  <br /><br />
                  Connect with as many neighbors as you’d like, sniff out the
                  connections that feel good, and journey together through the 3
                  Milestones of the Social Paws dog-sharing journey!
                </p>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">4</div>
                  <h4>Reviewing & Rating</h4>
                </div>
                <p className="cg-body-text">
                  After real-life interactions, members can review and rate both
                  dogs and humans. We encourage reviews because they:
                </p>
                <ul className="cg-bullet-list">
                  <li>keep the community accountable</li>
                  <li>support transparency</li>
                  <li>build trust</li>
                  <li>guide others toward safe and positive matches</li>
                </ul>
                <p className="cg-body-text" style={{ marginTop: "1rem" }}>
                  Thoughtful, respectful feedback strengthens Social Paws for
                  everyone.
                </p>
              </div>

              <div className="cg-section-divider">
                <h2>🚫 NAUGHTY COMMUNITY!</h2>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">5</div>
                  <h4>Dishonesty</h4>
                </div>
                <p className="cg-body-text">
                  It may feel uncomfortable to list that your dog is reactive,
                  nervous, or unfriendly with other dogs — but honesty is
                  critical. Being truthful doesn't make your dog a bad dog. It
                  makes you a responsible member of the community. Honesty is
                  the best pawlicy, and it ensures everyone gets the best,
                  safest matches possible.
                </p>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">6</div>
                  <h4>Disregarding Others’ Time</h4>
                </div>
                <p className="cg-body-text">
                  Social Paws facilitates introductions, but you are responsible
                  for showing up — fully and respectfully. We do not tolerate:
                </p>
                <ul className="cg-bullet-list">
                  <li>no-shows</li>
                  <li>last-minute cancellations without explanation</li>
                  <li>leaving a neighbor waiting</li>
                  <li>ghosting after making plans</li>
                </ul>
                <p className="cg-body-text" style={{ marginTop: "1rem" }}>
                  If you cannot make a scheduled walk: reschedule. If you're
                  running late: send a quick message. Respecting time shows
                  respect for the dog, the human, and the spirit of our
                  community.
                </p>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">7</div>
                  <h4>Animal Abuse</h4>
                </div>
                <p className="cg-body-text">
                  Dog-sharing gives insight into how dogs are treated in
                  everyday life. If you witness behavior that seems abusive,
                  unsafe, or inappropriate, report it. Your identity will be
                  protected, and we will take action.
                  <br />
                  We prioritize the safety and well-being of both dogs and
                  humans, with zero exceptions.
                </p>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">8</div>
                  <h4>Not Picking Up After a Dog</h4>
                </div>
                <p className="cg-body-text">
                  Whether it’s your dog or one you’re caring for, you’re
                  responsible for picking up after them. If you forgot bags:
                </p>
                <ul className="cg-bullet-list">
                  <li>ask a passerby</li>
                  <li>improvise</li>
                  <li>or return to the spot and follow through</li>
                </ul>
                <p className="cg-body-text" style={{ marginTop: "1rem" }}>
                  Leaving dog waste behind is disrespectful to your neighbors
                  and the shared spaces we all use. Responsible dog care =
                  cleaning up every time.
                </p>
              </div>

              <div className="cg-item">
                <div className="cg-item-header">
                  <div className="cg-number">9</div>
                  <h4>Pushing Outside Agendas</h4>
                </div>
                <p className="cg-body-text">Social Paws is not a place to:</p>
                <ul className="cg-bullet-list">
                  <li>sell products</li>
                  <li>promote services</li>
                  <li>recruit clients</li>
                  <li>advertise unrelated causes</li>
                  <li>redirect conversations toward outside agendas</li>
                </ul>
                <p className="cg-body-text" style={{ marginTop: "1rem" }}>
                  Keep the space focused on what it was built for: sharing dogs,
                  building trust, and strengthening community.
                </p>
              </div>

              <div className="cg-commitment-card">
                <div className="cg-commitment-title">
                  🌟 OUR COMMUNITY VALUES
                </div>
                <p className="cg-commitment-subtitle">
                  SOCIAL PAWS BELIEVES:
                </p>
                <ul className="cg-bullet-list cg-commitment-list">
                  <li>Pup Pawrents deserve more support.</li>
                  <li>Pup Pals deserve the companionship of a dog.</li>
                  <li>Neighborhoods are stronger when we bring back the village.</li>
                  <li>Everyone who loves dogs has a place in our community.</li>
                </ul>
              </div>

              <div className="cg-footer-message">
                <h4>Welcome to the pack!</h4>
                <p>
                  Together, let’s create a community where dogs are more loved,
                  humans more connected, and neighbors feel a little more like family.
                </p>
              </div>
            </div>
          </>
        )}

      {/* Global Footer */}
      {(activeView === "main" || activeView === "faq") && (
        <div className="help-footer">
          <span>
            Questions? Email us at{" "}
            <a
              href="mailto:woof@socialpawsapp.com"
              style={{
                fontWeight: "bold",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              woof@socialpawsapp.com
            </a>
          </span>
          <span>
            Feedback?{" "}
            <a
              href="https://forms.gle/Tc6S4mkY3UCWrFLk7"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontWeight: "bold",
                color: "inherit",
                cursor: "pointer",
              }}
            >
              Submit here
            </a>
          </span>
        </div>
      )}
      </div>
    </div>
  );
};

export default HelpPage;
